import React, { useState, useEffect } from 'react';
import { Table, Button, Space, message, Input, Modal, DatePicker } from 'antd';
import { SearchOutlined, ReloadOutlined, DownloadOutlined, FileTextOutlined, EyeOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

interface AnnouncementItem {
  id: number;
  project_no: string;
  project_name: string;
}

interface RegistrationListItem {
  id: number;
  seqNo: number;
  paymentTime: string;
  supplierName: string;
  contactName: string;
  contactPhone: string;
  email: string;
  biddingUrl: string;
  unionMember?: string;
}

interface RegistrationListData {
  projectName: string;
  projectNo: string;
  list: RegistrationListItem[];
}

export default function OtherAnnouncementRegistrationList() {
  const [announcements, setAnnouncements] = useState<AnnouncementItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [exportingId, setExportingId] = useState<number | null>(null);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [registrationData, setRegistrationData] = useState<RegistrationListData | null>(null);
  const [editingPaymentTime, setEditingPaymentTime] = useState<{ id: number; value: dayjs.Dayjs | null } | null>(null);

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const params = new URLSearchParams();
      if (searchKeyword) {
        params.append('keyword', searchKeyword);
      }
      
      const resp = await fetch(`/api/admin/other-announcement-registration-list?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json());

      if (resp && resp.ok) {
        setAnnouncements(resp.data || []);
      } else {
        message.error(resp?.error || '获取数据失败');
      }
    } catch (err) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchAnnouncements();
  };

  const handleExport = async (projectNo: string) => {
    try {
      setExportingId(announcements.find(a => a.project_no === projectNo)?.id || null);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`/api/admin/other-announcement-registration-list/export?project_no=${encodeURIComponent(projectNo)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `其他公告报名清单_${projectNo}_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        message.success('导出成功');
      } else {
        const result = await response.json();
        message.error(result?.error || '导出失败');
      }
    } catch (err) {
      message.error('导出失败');
    } finally {
      setExportingId(null);
    }
  };

  // 格式化时间
  const formatDateTime = (time: string | null) => {
    if (!time) return '-';
    return dayjs(time).format('YYYY-MM-DD HH:mm:ss');
  };

  // 查看清单
  const handleViewList = async (projectNo: string) => {
    try {
      setViewLoading(true);
      setViewModalVisible(true);
      const token = localStorage.getItem('admin_token');
      
      const response = await fetch(`/api/admin/other-announcement-registration-list/data?project_no=${encodeURIComponent(projectNo)}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.ok) {
          setRegistrationData(result.data);
        } else {
          message.error(result?.error || '获取数据失败');
          setViewModalVisible(false);
        }
      } else {
        const result = await response.json();
        message.error(result?.error || '获取数据失败');
        setViewModalVisible(false);
      }
    } catch (err) {
      message.error('获取数据失败');
      setViewModalVisible(false);
    } finally {
      setViewLoading(false);
    }
  };

  // 保存购买时间
  const handleSavePaymentTime = async () => {
    if (!editingPaymentTime || !editingPaymentTime.value) {
      message.error('请选择时间');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      const paymentTime = editingPaymentTime.value.format('YYYY-MM-DD HH:mm:ss');
      
      const response = await fetch(`/api/admin/trade-orders/${editingPaymentTime.id}/payment-time`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ payment_time: paymentTime }),
      });
      
      if (response.ok) {
        const result = await response.json();
        if (result.ok) {
          message.success('更新成功');
          // 更新本地数据
          if (registrationData) {
            const updatedList = registrationData.list.map(item => {
              if (item.id === editingPaymentTime.id) {
                return { ...item, paymentTime: paymentTime };
              }
              return item;
            });
            setRegistrationData({ ...registrationData, list: updatedList });
          }
          setEditingPaymentTime(null);
        } else {
          message.error(result?.error || '更新失败');
        }
      } else {
        const result = await response.json();
        message.error(result?.error || '更新失败');
      }
    } catch (err) {
      message.error('更新失败');
    }
  };

  // 下载投标文件
  const handleDownloadBiddingFile = async (record: RegistrationListItem) => {
    if (!record.biddingUrl) {
      message.error('文件不存在');
      return;
    }

    try {
      const token = localStorage.getItem('admin_token');
      
      // 记录下载
      await fetch(`/api/admin/other-announcement-registration-list/record-download`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ order_id: record.id }),
      });

      // 下载文件（直接使用a标签下载）
      const link = document.createElement('a');
      link.href = record.biddingUrl;
      // 从URL中提取文件名
      const urlParts = record.biddingUrl.split('/');
      const fileName = urlParts[urlParts.length - 1] || 'bidding_file';
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success('下载成功');
    } catch (err) {
      message.error('下载失败');
    }
  };

  // 清单表格列定义
  const listColumns: ColumnsType<RegistrationListItem> = [
    {
      title: '序号',
      dataIndex: 'seqNo',
      key: 'seqNo',
      width: 80,
      align: 'center',
    },
    {
      title: '购买时间',
      dataIndex: 'paymentTime',
      key: 'paymentTime',
      width: 180,
      render: (text: string, record: RegistrationListItem) => {
        if (editingPaymentTime?.id === record.id) {
          return (
            <DatePicker
              showTime
              format="YYYY-MM-DD HH:mm:ss"
              value={editingPaymentTime.value}
              onChange={(value) => setEditingPaymentTime({ id: record.id, value })}
              onOk={handleSavePaymentTime}
              style={{ width: '100%' }}
            />
          );
        }
        return (
          <span
            style={{ cursor: 'pointer', color: '#3b82f6' }}
            onClick={() => setEditingPaymentTime({ id: record.id, value: text ? dayjs(text) : null })}
          >
            {formatDateTime(text)}
          </span>
        );
      },
    },
    {
      title: '供应商名称',
      dataIndex: 'supplierName',
      key: 'supplierName',
      width: 200,
    },
    {
      title: '联系人',
      dataIndex: 'contactName',
      key: 'contactName',
      width: 120,
    },
    {
      title: '联系方式',
      dataIndex: 'contactPhone',
      key: 'contactPhone',
      width: 140,
    },
    {
      title: '邮箱',
      dataIndex: 'email',
      key: 'email',
      width: 200,
    },
    {
      title: '是否已上传投标文件',
      dataIndex: 'biddingUrl',
      key: 'biddingUrl',
      width: 180,
      align: 'center',
      render: (text: string, record: RegistrationListItem) => {
        if (text && text.trim() !== '') {
          return (
            <Button
              type="link"
              onClick={() => handleDownloadBiddingFile(record)}
              style={{ color: '#3b82f6', padding: 0 }}
            >
              点击下载
            </Button>
          );
        }
        return <span style={{ color: '#ef4444' }}>未上传</span>;
      },
    },
  ];

  const columns: ColumnsType<AnnouncementItem> = [
    {
      title: '项目编号',
      dataIndex: 'project_no',
      key: 'project_no',
      width: 180,
      render: (text) => <span style={{ fontFamily: 'monospace', color: '#1f2937' }}>{text}</span>
    },
    {
      title: '项目名称',
      dataIndex: 'project_name',
      key: 'project_name',
      width: 280,
      ellipsis: true,
      render: (text) => <span style={{ fontWeight: 500, color: '#1f2937' }}>{text}</span>
    },
    {
      title: '操作',
      key: 'action',
      width: 240,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="default"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewList(record.project_no)}
            style={{ borderRadius: 8 }}
          >
            查看清单
          </Button>
          <Button
            type="primary"
            size="small"
            icon={<DownloadOutlined />}
            onClick={() => handleExport(record.project_no)}
            loading={exportingId === record.id}
            style={{ 
              borderRadius: 8,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              border: 'none'
            }}
          >
            导出报名清单
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div className="admin-page-title">
          <div className="admin-page-title-icon">
            <FileTextOutlined />
          </div>
          <div>
            <h2>其他公告报名清单</h2>
            <p>查看其他公告的供应商报名情况并导出清单</p>
          </div>
        </div>
        
        <Space size="middle">
          <Input
            placeholder="搜索项目编号/名称"
            prefix={<SearchOutlined style={{ color: '#9ca3af' }} />}
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
            onPressEnter={handleSearch}
            style={{ width: 220, borderRadius: 10 }}
            allowClear
          />
          <Button 
            icon={<ReloadOutlined />} 
            onClick={fetchAnnouncements}
            style={{ borderRadius: 10 }}
          >
            刷新
          </Button>
        </Space>
      </div>
      
      {/* 表格内容 */}
      <div className="admin-page-content">
        <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
          共 <span style={{ color: '#3b82f6', fontWeight: 600 }}>{announcements.length}</span> 条记录
        </div>
        
        <Table
          columns={columns}
          dataSource={announcements}
          loading={loading}
          rowKey="id"
          className="admin-table"
          scroll={{ x: 900 }}
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
          }}
        />
      </div>

      {/* 查看清单弹窗 */}
      <Modal
        title={
          <div style={{ fontSize: 18, fontWeight: 600 }}>
            购买标书供应商汇总表
          </div>
        }
        open={viewModalVisible}
        onCancel={() => {
          setViewModalVisible(false);
          setRegistrationData(null);
          setEditingPaymentTime(null);
        }}
        footer={null}
        width={1200}
        destroyOnClose
      >
        {registrationData && (
          <div>
            {/* 表头信息 */}
            <div style={{ marginBottom: 16, padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: 8 }}>
              <div style={{ marginBottom: 8, fontSize: 14, color: '#374151' }}>
                <strong>项目名称：</strong>{registrationData.projectName}
              </div>
              <div style={{ fontSize: 14, color: '#374151' }}>
                <strong>项目编号：</strong>{registrationData.projectNo}
              </div>
            </div>

            {/* 数据表格 */}
            <Table
              columns={listColumns}
              dataSource={registrationData.list}
              loading={viewLoading}
              rowKey="id"
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`,
              }}
              scroll={{ x: 1000 }}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}

